import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { FormattedMessage, intlShape } from 'react-intl';
import { map, get, isEmpty, find, mapKeys } from 'lodash';
import SelectField from 'shared-components/reduxForms/fields/SelectField/SelectField';
import TextField from 'shared-components/reduxForms/fields/TextField/TextField';

const useFormattedDivisions = (selectedCountry = {}, zoneExceptionList) => {
  const formattedDivisions = useMemo(() => {
    const keyMap = { code: 'value', name: 'label' };
    const divisions = get(selectedCountry, 'zones', []).filter(zone => !zoneExceptionList.includes(zone.code));
    return divisions.map(division => mapKeys(division, (value, key) => keyMap[key]));
  }, [selectedCountry.code]);

  const isCountryWithProvinces = !isEmpty(get(selectedCountry, 'zones'));

  return { formattedDivisions, isCountryWithProvinces };
};

const useCountryOptions = (allCountries) => {
  return useMemo(() => {
    return map(allCountries, country => {
      return { value: country.code, label: country.name };
    });
  }, [allCountries]);
};

export const TaxesBillingAddress = ({
  onChangeFn = null,
  onSubRegionChange = null,
  allCountries,
  billingCountryValue,
  defaultCountry,
  intl
}) => {
  const selectedCountry = useMemo(() => {
    return find(allCountries, (country) => {
      return get(country, 'code') === billingCountryValue;
    });
  }, [billingCountryValue, allCountries]);

  const countryOptions = useCountryOptions(allCountries);

  const { formattedDivisions, isCountryWithProvinces } = useFormattedDivisions(selectedCountry, ['BFP']);

  return (
    <div className="TaxesBillingAddress">
      <div className="message-host-section">
        <div className="flex space-between margin-lg-bottom">
          <div className="full-width">
            <div className="flex middle h6 no-top-margin margin-xxl-bottom">
              <img className="icon margin-md-right" src="https://assets.peerspace.com/credit-card.svg?v=rb" alt={ intl.formatMessage({ id: 'review_pay_form.credit_card_icon', defaultMessage: 'Credit Card Icon' }) } />
              <FormattedMessage
                id="review_pay_form.billing_address_title"
                defaultMessage="Billing Address"
              />
            </div>
            <div className="flex flex-wrap fields-container">
              <div className="flex full-width field-full-row">
                <div className="field-row">
                  <div className="heavier margin-md-bottom">
                    <FormattedMessage id='review_pay_form.billing_country' defaultMessage="Your billing country" />
                  </div>
                  <div>
                    <Field
                      component={SelectField}
                      name="billing_country"
                      className="form-fields"
                      placeholder={ intl.formatMessage({ id: 'review_pay_form.select_country', defaultMessage: 'Select a country' }) }
                      options={countryOptions}
                      onChange={onChangeFn}
                      maxMenuHeight={260}
                      isSearchable={true}
                      isDisabled={!!defaultCountry}
                      attributes={{ 'data-testing-id': 'billing_country' }}
                    />
                  </div>
                </div>
              </div>

              <div className="field-row">
                <div className="heavier margin-md-bottom">
                  {get(selectedCountry, 'labels.address1', <FormattedMessage id="review_pay_form.street_address" defaultMessage="Street address"/>)}
                </div>
                <div>
                  <Field
                    component={TextField}
                    name="billing_street"
                    id="billing-street"
                    className="form-fields"
                    attributes={{ 'data-testing-id': 'billing_street' }}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="heavier margin-md-bottom">
                  {get(selectedCountry, 'labels.street2') || <FormattedMessage id='review_pay_form.billing_unit' defaultMessage="Apartment, suite, etc." />}
                </div>
                <div>
                  <Field
                    component={TextField}
                    name="billing_unit"
                    id="billing-unit"
                    className="form-fields"
                    attributes={{ 'data-testing-id': 'billing_unit' }}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="heavier margin-md-bottom">
                  {get(selectedCountry, 'labels.city') || <FormattedMessage id='review_pay_form.billing_city' defaultMessage="City" />}
                </div>
                <div>
                  <Field
                    component={TextField}
                    name="billing_city"
                    id="billing-city"
                    className="form-fields"
                    attributes={{ 'data-testing-id': 'billing_city' }}
                  />
                </div>
              </div>

              {isCountryWithProvinces && (
                <div className="field-row">
                  <div className="heavier margin-md-bottom">
                    {get(selectedCountry, 'labels.zone')}
                  </div>
                  <div>
                    <Field
                      component={SelectField}
                      options={formattedDivisions}
                      name="billing_state"
                      defaultValue={formattedDivisions[0].value}
                      onChange={onSubRegionChange}
                    />
                  </div>
                </div>
              )}

              <div className="field-row">
                <div className="heavier margin-md-bottom">
                  {get(selectedCountry, 'labels.postalCode') || <FormattedMessage id='review_pay_form.postal_code' defaultMessage="Postal Code" />}
                </div>
                <div>
                  <Field
                    component={TextField}
                    name="billing_zip"
                    id="billing-zip"
                    className="form-fields"
                    attributes={{ 'data-testing-id': 'billing_zip' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

TaxesBillingAddress.propTypes = {
  intl: intlShape,
  onChangeFn: PropTypes.func,
  onSubRegionChange: PropTypes.func,
  allCountries: PropTypes.array,
  billingCountryValue: PropTypes.string
};
